import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Point, Position, CapturedEmoji, EmojiGameConfig, EmojiGameLevel, RescueEffect as RescueEffectType, EmojiTheme, CelebratingEmoji as CelebratingEmojiType, SuperEmojiCollection, SuperEmojiEffect } from '../utils/types';
import { gameAudio } from '../services/audioService';
import GameHeader from '../components/game/GameHeader';
import TapEffect from '../components/game/TapEffect';
import RescueEffect from '../components/game/RescueEffect';
import GameStartScreen from './GameStartScreen';
import GameOverScreen from './GameOverScreen';
import GameExpiredScreen from './GameExpiredScreen';
import GameStyles from '../components/game/GameStyles';
import GameConfetti from '../components/game/GameConfetti';
import GameCanvas from '../components/game/GameCanvas';
import GameBackground from '../components/game/GameBackground';
import GameContainer from '../components/game/GameContainer';
import { gameService } from '../services/gameService';
import { emojiRescueService } from '../services/emojiRescueService';
import MemoizedEmoji from '../components/flying-object/MemoizedEmoji';
import CelebratingEmoji from '../components/game/CelebratingEmoji';
import { LevelService } from '../services/levelService';
import { levelDbService } from '../services/levelDbService';
import { getThemeForLevel } from '../utils/emojiThemes';
import { superEmojiService } from '../services/superEmojiService';
import { superEmojiPowers } from '../services/superEmojiPowers';
import SuperEmojiEffects from '../components/game/SuperEmojiEffects';
import GameFooter from '../components/game/GameFooter';

export default function PlayGame() {
  const navigate = useNavigate();
  const { id, sessionId } = useParams();

  console.log('useParams values:', { id, sessionId });

  // Determine if it's auto mode (either old /game/auto or new /game/auto/:sessionId)
  const isAutoMode = id === 'auto' || !!sessionId; 
  // Determine the actual game ID to use for fetching/updating user-created games
  const actualGameId = isAutoMode ? sessionId : id;

  const lastTapTime = useRef(0);
  const tapCooldown = 250; // milliseconds

  const [isPlaying, setIsPlaying] = useState(false);
  const [gameStatus, setGameStatus] = useState<'playing' | 'won' | 'lost' | 'idle' | 'expired'>('idle');
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animationRef = useRef<number | null>(null);
  const gameStatusRef = useRef(gameStatus);
  const [totalAttempts, setTotalAttempts] = useState(0);
  const [successfulAttempts, setSuccessfulAttempts] = useState(0);
  const [tapEffect, setTapEffect] = useState({ x: 0, y: 0, visible: false, tapSign: '☠️' });
  const [progress, setProgress] = useState(0);
  const tapEffectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [windowSize, setWindowSize] = useState({
    width: window.innerWidth,
    height: window.innerHeight
  });

  // Add these missing state declarations
  const [playerName, setPlayerName] = useState<string | null>(null);
  const [isSinglePlay, setIsSinglePlay] = useState<boolean>(false);
  const [winMessage, setWinMessage] = useState<string | null>(null);
  const [loseMessage, setLoseMessage] = useState<string | null>(null);

  // UPDATED: Change from Bee[] to CapturedEmoji[] but keep both for transition
  const [emojis, setEmojis] = useState<CapturedEmoji[]>([]);
  const emojisRef = useRef<CapturedEmoji[]>([]);
  
  useEffect(() => {
    emojisRef.current = emojis;
  }, [emojis]);

  // Game configuration
  const [gameConfig, setGameConfig] = useState<EmojiGameConfig | null>(null);
  
  const [gameStartTime, setGameStartTime] = useState<number>(0);
  const lastFrameTime = useRef(performance.now());
  const FRAME_INTERVAL = 1000 / 60; // 60 FPS

  const [currentLevel, setCurrentLevel] = useState(1);
  const [autoGameState, setAutoGameState] = useState({
    currentLevel: 1,
    highestLevel: 1,
    isLevelComplete: false,
    levelStartTime: 0
  });

  // Enable emoji rescue mode (always enabled now)
  const [currentTheme, setCurrentTheme] = useState<EmojiTheme>(getThemeForLevel(1));
  const [rescueEffects, setRescueEffects] = useState<RescueEffectType[]>([]);
  
  // Track rescued emojis collection as counts across the session
  const [rescuedEmojis, setRescuedEmojis] = useState<Record<string, number>>({});
  
  // NEW: State for celebrating emojis at the bottom
  const [celebratingEmojis, setCelebratingEmojis] = useState<CelebratingEmojiType[]>([]);
  
  // NEW: State for tracking continuous spawning
  const [spawnedEmojiCount, setSpawnedEmojiCount] = useState<number>(0);
  const [nextEmojiIndex, setNextEmojiIndex] = useState<number>(0);
  const [currentLevelRescuedCount, setCurrentLevelRescuedCount] = useState<number>(0);
  const celebratingIdCounter = useRef<number>(0);
  
  // NEW: State for tracking expiring UFOs
  const [expiringUfoIds, setExpiringUfoIds] = useState<Set<string>>(new Set());
  
  // NEW: Track which emojis have already been celebrated to prevent duplicates (using ref for synchronous updates)
  const celebratedEmojiIdsRef = useRef<Set<string>>(new Set());
  
  // Super Emoji states
  const [superEmojiCollection, setSuperEmojiCollection] = useState<SuperEmojiCollection>({});
  const [superEmojiEffects, setSuperEmojiEffects] = useState<SuperEmojiEffect[]>([]);
  const [isSuperEmojiPaused, setIsSuperEmojiPaused] = useState<boolean>(false);
  const isSuperEmojiPausedRef = useRef<boolean>(false);
  const pausedEmojiStates = useRef<CapturedEmoji[]>([]);
  const pauseStartTime = useRef<number>(0);
  
  // Function to remove celebrating emoji when done
  const removeCelebratingEmoji = useCallback((id: string) => {
    setCelebratingEmojis(prev => prev.filter(emoji => emoji.id !== id));
  }, []);

  // Super emoji pause/resume functions
  const pauseGameForSuperEmoji = useCallback(() => {
    if (isSuperEmojiPausedRef.current) return; // Already paused
    
    console.log('🛑 Pausing game for super emoji power');
    
    // Store current time and emoji states
    pauseStartTime.current = performance.now();
    pausedEmojiStates.current = [...emojisRef.current];
    
    // Set pause state
    setIsSuperEmojiPaused(true);
    isSuperEmojiPausedRef.current = true;
  }, []);

  const resumeGameAfterSuperEmoji = useCallback(() => {
    console.log(`🔍 Resume function called. isSuperEmojiPausedRef: ${isSuperEmojiPausedRef.current}`);
    if (!isSuperEmojiPausedRef.current) {
      console.log('❌ Not resuming - game is not paused');
      return; // Not paused
    }
    
    console.log('▶️ Resuming game after super emoji power');
    
    const pauseDuration = performance.now() - pauseStartTime.current;
    console.log(`⏰ Pause duration was: ${pauseDuration}ms`);
    
    // Adjust start times for all remaining emojis to account for pause
    setEmojis(prevEmojis => {
      return prevEmojis.map(emoji => {
        if (emoji.status === 'captured') {
          return {
            ...emoji,
            startTime: emoji.startTime + pauseDuration
          };
        }
        return emoji;
      });
    });
    
    // Clear pause state
    setIsSuperEmojiPaused(false);
    isSuperEmojiPausedRef.current = false;
    pausedEmojiStates.current = [];
    pauseStartTime.current = 0;
  }, []);

  // Helper function to simulate full tap behavior for super emoji powers
  const simulateUFOTap = useCallback((ufoId: string) => {
    const now = performance.now();
    const currentEmojis = emojisRef.current;
    const tappedEmoji = currentEmojis.find(e => e.id === ufoId);
    
    if (!tappedEmoji || tappedEmoji.status !== 'captured') return;
    
    console.log(`🌟 Super power destroying UFO: ${tappedEmoji.emoji} (ID: ${ufoId})`);
    
    // Increment current level rescued count (but don't add to collection - super powers don't count for collection)
    setCurrentLevelRescuedCount(prev => prev + 1);

    // Add rescue effect (visual burst)
    const effectId = `super-${now}-${Math.random().toString(36).substr(2, 9)}`;
    const newRescueEffect: RescueEffectType = {
      stage: 'ufo_break',
      emoji: tappedEmoji.emoji,
      position: tappedEmoji.position,
      visible: true,
      timestamp: now,
      id: effectId
    };
    
    console.log('🎆 Super power triggering rescue effect for:', tappedEmoji.emoji, 'at position:', tappedEmoji.position);
    setRescueEffects(prev => [...prev, newRescueEffect]);

    // Update emoji state (change status to falling)
    setEmojis(prevEmojis => emojiRescueService.handleEmojiTap(prevEmojis, ufoId));
  }, []);

  // Super emoji power activation handler
  const handleSuperEmojiActivation = useCallback(async (emoji: string) => {
    if (gameStatus !== 'playing' || !isPlaying || isSuperEmojiPausedRef.current) return;
    
    const updatedCollection = superEmojiService.useFromCollection(superEmojiCollection, emoji);
    if (!updatedCollection) {
      console.log(`❌ Cannot use ${emoji} - not enough in collection`);
      return;
    }
    
    setSuperEmojiCollection(updatedCollection);
    console.log(`🌟 Activated ${emoji} power! Remaining: ${updatedCollection[emoji] || 0}`);
    
    // 🛑 PAUSE GAME for dramatic effect
    pauseGameForSuperEmoji();
    
    // Get current UFOs for power activation
    const currentUfos = emojisRef.current;
    
    // Create power-specific effect
    let effect: SuperEmojiEffect;
    
    switch (emoji) {
      case '💃':
        effect = superEmojiPowers.activateDance(windowSize, currentUfos, simulateUFOTap);
        break;
      case '⚔️':
        effect = superEmojiPowers.activateSword(windowSize, currentUfos, simulateUFOTap);
        break;
      case '🪂':
        effect = superEmojiPowers.activateParachute(windowSize, currentUfos, simulateUFOTap);
        break;
      case '💣':
        effect = superEmojiPowers.activateBomb(currentUfos, simulateUFOTap);
        break;
      case '❤️‍🔥':
        effect = superEmojiPowers.activateFireHeart(currentUfos, simulateUFOTap, () => {
          // Complete level immediately
          setGameStatus('won');
        });
        break;
      default:
        console.warn(`Unknown super emoji: ${emoji}`);
        return;
    }
    
    // Add visual effect
    setSuperEmojiEffects(prev => [...prev, effect]);
    
    // Remove effect and resume game after duration
    console.log(`⏰ Setting timeout for ${effect.duration}ms to resume game after ${emoji} effect`);
    setTimeout(() => {
      console.log(`⏰ Timeout fired! Removing effect and resuming game for ${emoji}`);
      setSuperEmojiEffects(prev => prev.filter(e => e.id !== effect.id));
      // ▶️ RESUME GAME after effect completes
      resumeGameAfterSuperEmoji();
    }, effect.duration);
    
  }, [gameStatus, isPlaying, superEmojiCollection, windowSize, simulateUFOTap, pauseGameForSuperEmoji, resumeGameAfterSuperEmoji]);

  // Add a new state to track level transition
  const [isTransitioning, setIsTransitioning] = useState(false);

  // Add a new state to track confetti
  const [showConfetti, setShowConfetti] = useState(false);

  // Add a new state for level completion message
  const [levelCompletionMessage, setLevelCompletionMessage] = useState<string>('');

  // Add a new state to track if we're showing level completion
  const [showingLevelCompletion, setShowingLevelCompletion] = useState(false);

  // Add a new state to track if this is the first level
  const [isFirstLevel, setIsFirstLevel] = useState(true);

  // Update theme when level changes
  useEffect(() => {
    const theme = getThemeForLevel(currentLevel);
    setCurrentTheme(theme);
  }, [currentLevel]);

  // Add effect to load level session data
  useEffect(() => {
    const loadLevelSession = async () => {
      if (isAutoMode && sessionId) {
        try {
          const session = await levelDbService.getLevelSession(sessionId);
          if (session) {
            setCurrentLevel(session.currentLevel);
            setAutoGameState(prev => ({
              ...prev,
              currentLevel: session.currentLevel,
              highestLevel: session.highestLevel
            }));
            
            // Load existing emoji collection from session
            if (session.rescuedEmojis) {
              setRescuedEmojis(session.rescuedEmojis);
              const totalCount = Object.values(session.rescuedEmojis).reduce((sum, count) => sum + count, 0);
              const emojiList = Object.entries(session.rescuedEmojis)
                .map(([emoji, count]) => `${emoji}×${count}`)
                .join(', ');
              console.log(`📋 Loaded emoji collection from session: ${emojiList} (${totalCount} total)`);
            }
            
            // Load existing super emoji collection from session
            if (session.superEmojiCollection) {
              setSuperEmojiCollection(session.superEmojiCollection);
              const totalCount = Object.values(session.superEmojiCollection).reduce((sum, count) => sum + count, 0);
              const emojiList = Object.entries(session.superEmojiCollection)
                .map(([emoji, count]) => `${emoji}×${count}`)
                .join(', ');
              console.log(`🌟 Loaded super emoji collection from session: ${emojiList} (${totalCount} total)`);
            }
            
            // Play ready level sound when reloading existing game
            try {
              await gameAudio.initialize();
              await gameAudio.playReadyLevelSound();
              console.log('Ready level audio started for reloaded game');
            } catch (error) {
              console.error('Error playing ready level sound for reloaded game:', error);
            }
          }
        } catch (error) {
          console.error('Error loading level session:', error);
          // Fallback to level 1 if session loading fails
          setCurrentLevel(1);
          setAutoGameState(prev => ({
            ...prev,
            currentLevel: 1,
            highestLevel: 1
          }));
        }
      }
    };

    loadLevelSession();
  }, [isAutoMode, sessionId]);

  // Update handleStartNextLevel to persist session data
  const handleStartNextLevel = useCallback(async () => {
    const nextLevel = LevelService.getNextLevel(currentLevel);
    // Removed nextLevelConfig - no longer needed since we removed tap limits
    
    // Reset all states
    setShowConfetti(false);
    setLevelCompletionMessage('');
    setShowingLevelCompletion(false);
    setGameStatus('idle');
    
    // Stop any playing game status sounds
    gameAudio.stopGameStatusSounds();
    
    // Update level state
    setCurrentLevel(nextLevel);
    setAutoGameState(prev => ({
      ...prev,
      currentLevel: nextLevel,
      isLevelComplete: false
    }));

    // Update session data if in session mode
    if (isAutoMode && sessionId) {
      await levelDbService.updateLevelSession(sessionId, {
        currentLevel: nextLevel,
        highestLevel: Math.max(currentLevel, nextLevel)
      });
    }

    // Reset game state for next level (but preserve rescued emoji collection)
    setEmojis([]);
    // Removed remainingTaps - no tap limit
    setProgress(0);
    // Reset spawning counters for new level
    setSpawnedEmojiCount(0);
    setNextEmojiIndex(0);
    setCurrentLevelRescuedCount(0);
    celebratingIdCounter.current = 0;
    // Note: rescuedEmojis is NOT reset - it carries forward across levels
    
    // Start the game immediately for level transitions
    const startTime = performance.now();
    setGameStartTime(startTime);

    try {
      // Initialize the next level
      const { paths, config } = await LevelService.initializeLevelWithEmojis(
        nextLevel,
        windowSize
      );
      setGameConfig(config);
      const initialBees = emojiRescueService.initializeCapturedEmojisWithIntervals(
        paths,
        startTime,
        LevelService.getEmojiLevelConfig(nextLevel)
      );
      setEmojis(initialBees);
      // Initialize spawning counter with the number of initial emojis
      setSpawnedEmojiCount(initialBees.length);
      setNextEmojiIndex(initialBees.length);
      
      // Start playing
      setIsPlaying(true);
      setGameStatus('playing');
      await gameAudio.playBackgroundMusic();
    } catch (error) {
      console.error('Error starting next level:', error);
      setIsPlaying(false);
    }
  }, [currentLevel, windowSize, isAutoMode, sessionId]);

  // Add this useEffect for loading stats
  useEffect(() => {
    const loadStats = async () => {
      // Use actualGameId for loading stats
      const stats = await gameService.loadGameStats(actualGameId);
      if (stats) {
        setTotalAttempts(stats.totalAttempts);
        setSuccessfulAttempts(stats.successfulAttempts);
      }
    };

    loadStats();
  }, [actualGameId]);

  useEffect(() => {
    gameStatusRef.current = gameStatus;
  }, [gameStatus]);

  // Update game status effect to handle level progression
  useEffect(() => {
    if (gameStatus === 'won') {
      setShowConfetti(true);
      setShowingLevelCompletion(true);
      setIsPlaying(false); // Stop the game loop to show the banner

      if (isAutoMode) {
        if (!LevelService.hasMoreLevels(currentLevel)) {
          setLevelCompletionMessage('🎉 Congratulations! You have completed all levels! 🎉');
        } else {
          setLevelCompletionMessage(`🎉 Level ${currentLevel} Completed! 🎉`);
        }
      } else { // For user-generated games
        setLevelCompletionMessage(winMessage || "Congratulations! You Won!");
      }
    } else if (gameStatus === 'lost') {
      setShowingLevelCompletion(true);
      setIsPlaying(false); // Stop the game loop to show the banner
      setLevelCompletionMessage(loseMessage || "Never Give In.. Never Give Up.."); // More descriptive loss message
      // Explicitly unset customButton for lost state if it was set for auto mode wins
      // This is handled by the rendering logic directly, as customButton is only passed for auto mode wins
    }
  }, [gameStatus, isAutoMode, currentLevel, winMessage, loseMessage]);

  // NEW: Function to spawn a new emoji if needed
  const spawnNewEmojiIfNeeded = useCallback(async () => {
    if (!gameConfig || !isAutoMode || isSuperEmojiPausedRef.current) return;
    
    const currentEmojisCount = emojisRef.current.length;
    const { totalEmojisForLevel, maxConcurrentEmojis } = gameConfig;
    
    // Check if we should spawn more emojis
    const totalEmojisSpawned = currentLevelRescuedCount + currentEmojisCount;
    const shouldSpawn = (
      totalEmojisSpawned < totalEmojisForLevel && // Haven't spawned all emojis for this level yet
      currentEmojisCount < maxConcurrentEmojis // Room for more on screen
    );
    
    console.log(`🎯 Spawn check: rescued=${currentLevelRescuedCount}, onScreen=${currentEmojisCount}, totalSpawned=${totalEmojisSpawned}/${totalEmojisForLevel}, shouldSpawn=${shouldSpawn}`);
    
    if (shouldSpawn) {
      console.log(`🚀 SPAWNING new emoji: ${spawnedEmojiCount + 1}/${totalEmojisForLevel}, ${currentEmojisCount}/${maxConcurrentEmojis} on screen`);
      
      try {
        const windowSize = { width: window.innerWidth, height: window.innerHeight };
        const newEmoji = await emojiRescueService.spawnNewEmoji(
          currentTheme,
          performance.now(),
          nextEmojiIndex,
          windowSize
        );
        
        setEmojis(prev => [...prev, newEmoji]);
        setSpawnedEmojiCount(prev => prev + 1);
        setNextEmojiIndex(prev => prev + 1);
        
        console.log(`✅ Spawned emoji ${newEmoji.emoji} with ID ${newEmoji.id}`);
      } catch (error) {
        console.error('Failed to spawn new emoji:', error);
      }
    }
  }, [gameConfig, isAutoMode, spawnedEmojiCount, nextEmojiIndex, currentTheme, currentLevelRescuedCount]);

  // UPDATED: Enhanced tap handler for emoji rescue
  const handleTap = useCallback((e: React.TouchEvent | React.MouseEvent, emojiId: string) => {
    if (gameStatus !== 'playing' || !isPlaying) return;

    const now = Date.now();
    lastTapTime.current = now;

    // First, check if the emoji can be tapped (without state changes)
    const currentEmojis = emojisRef.current;
    const tappedEmoji = currentEmojis.find(b => b.id === emojiId);
    if (!tappedEmoji || tappedEmoji.status !== 'captured') return;

    console.log(`🎯 Tapping emoji ${tappedEmoji.emoji} (ID: ${emojiId})`);

    // Check if this is a super emoji and add to super collection
    if (tappedEmoji.isSuperEmoji && superEmojiService.isSuperEmoji(tappedEmoji.emoji)) {
      setSuperEmojiCollection(prev => {
        const updated = superEmojiService.addToCollection(prev, tappedEmoji.emoji);
        console.log(`🌟 Super emoji ${tappedEmoji.emoji} added to collection! New count: ${updated[tappedEmoji.emoji]}`);
        return updated;
      });
    } else {
      // Add rescued emoji to regular collection immediately (only once)
      setRescuedEmojis(prev => {
        const updatedCounts = { ...prev };
        const emoji = tappedEmoji.emoji;
        updatedCounts[emoji] = (updatedCounts[emoji] || 0) + 1;
        
        const totalCount = Object.values(updatedCounts).reduce((sum, count) => sum + count, 0);
        const emojiList = Object.entries(updatedCounts)
          .map(([emoji, count]) => `${emoji}×${count}`)
          .join(', ');
        console.log(`📦 Rescued emojis collection updated: ${emojiList} (${totalCount} total)`);
        
        return updatedCounts;
      });
    }

    // Increment current level rescued count
    setCurrentLevelRescuedCount(prev => prev + 1);

    // Add new rescue effect
    const effectId = `${now}-${Math.random().toString(36).substr(2, 9)}`;
    const newRescueEffect: RescueEffectType = {
      stage: 'ufo_break',
      emoji: tappedEmoji.emoji,
      position: tappedEmoji.position,
      visible: true,
      timestamp: now,
      id: effectId
    };
    
    console.log('Triggering rescue effect for emoji:', tappedEmoji.emoji, 'at position:', tappedEmoji.position, 'with ID:', effectId);
    setRescueEffects(prev => [...prev, newRescueEffect]);

    // Update emoji state (change status to falling)
    setEmojis(prevEmojis => {
      return emojiRescueService.handleEmojiTap(prevEmojis, emojiId);
    });

    // Check if we need to spawn more emojis after this rescue
    if (isAutoMode && gameConfig?.totalEmojisForLevel) {
      console.log(`🎯 Post-tap spawn check: currentLevelRescuedCount will be ${currentLevelRescuedCount + 1}`);
      setTimeout(() => {
        spawnNewEmojiIfNeeded();
      }, 100); // Small delay to let state updates settle
    }

    // Play rescue sequence audio
    gameAudio.playRescueSequence();
  }, [gameStatus, isPlaying, isAutoMode, gameConfig, currentLevelRescuedCount, spawnNewEmojiIfNeeded]);

  // UPDATED: Enhanced animation loop for emoji rescue
  useEffect(() => {
    if (!isPlaying || emojisRef.current.length === 0 || !gameConfig) return;

    const animate = (currentTime: number) => {
      // Check if game is paused for super emoji
      if (isSuperEmojiPausedRef.current) {
        animationRef.current = requestAnimationFrame(animate);
        return;
      }

      // Throttle frame rate
      if (currentTime - lastFrameTime.current < FRAME_INTERVAL) {
        animationRef.current = requestAnimationFrame(animate);
        return;
      }
      lastFrameTime.current = currentTime;

      // Update progress
              const progress = emojiRescueService.calculateProgress(currentTime, gameStartTime, gameConfig.totalDuration);
      setProgress(progress);

      // Get current window size
      const currentSize = {
        width: window.innerWidth,
        height: window.innerHeight
      };

      // Update positions with current window size and handle bottom celebrations
      setEmojis(prevItems => {
        const updatedEmojis = emojiRescueService.updateEmojiPositions(prevItems, currentTime, currentSize);
        
        // Check for emojis that just hit the bottom (transitioned from 'falling' to 'rescued')
        const newBottomHits = updatedEmojis.filter((emoji, index) => {
          const prevEmoji = prevItems[index];
          const justHitBottom = prevEmoji && 
                               prevEmoji.status === 'falling' && 
                               emoji.status === 'rescued' && 
                               emoji.isActive === false; // Just became inactive (hit bottom)
          
          // Only create celebrating emoji if this emoji hasn't been celebrated yet
          return justHitBottom && !celebratedEmojiIdsRef.current.has(emoji.id);
        });
        
        // Create celebrating emojis for new bottom hits
        if (newBottomHits.length > 0) {
          const newCelebratingEmojis = newBottomHits.map(emoji => {
            celebratingIdCounter.current += 1;
            return {
              id: `celebrating-${emoji.id}-${currentTime}-${celebratingIdCounter.current}`,
              emoji: emoji.emoji,
              position: { x: emoji.position.x, y: currentSize.height - 20 }, // Near bottom
              bouncePhase: 0,
              timestamp: currentTime
            };
          });
          
          // Mark these emojis as celebrated to prevent duplicates
          newBottomHits.forEach(emoji => celebratedEmojiIdsRef.current.add(emoji.id));
          
          setCelebratingEmojis(prev => [...prev, ...newCelebratingEmojis]);
          
          console.log(`🎉 ${newBottomHits.length} emoji(s) hit the bottom and started celebrating!`);
          newBottomHits.forEach(emoji => {
            console.log(`  - ${emoji.emoji} at position (${Math.round(emoji.position.x)}, ${Math.round(emoji.position.y)})`);
          });
        }
        
        // Clean up vanished UFOs from memory (remove rescued emojis older than 1 second)
        const UFO_CLEANUP_DELAY = 1000; // milliseconds
        const cleanedEmojis = updatedEmojis.filter(emoji => {
          if (emoji.status === 'rescued' && emoji.rescuedTime) {
            const timeSinceRescue = currentTime - emoji.rescuedTime;
            return timeSinceRescue < UFO_CLEANUP_DELAY;
          }
          return true; // Keep all non-rescued emojis
        });
        
        // Check if any emojis were cleaned up and spawn new ones if needed
        const cleanedUpCount = updatedEmojis.length - cleanedEmojis.length;
        if (cleanedUpCount > 0 && isAutoMode) {
          console.log(`🧹 Cleaned up ${cleanedUpCount} emoji(s), checking if we should spawn more...`);
          // Use setTimeout to avoid state updates during render
          setTimeout(() => {
            spawnNewEmojiIfNeeded();
          }, 0);
        }
        
        // Track expiring UFOs for pulse effect
        const newExpiringUfoIds = new Set<string>();
        cleanedEmojis.forEach(emoji => {
          if (emoji.status === 'captured' && emoji.isActive) {
            const isExpiringSoon = emojiRescueService.isEmojiExpiringSoon(emoji, currentTime);
            if (isExpiringSoon) {
              newExpiringUfoIds.add(emoji.id);
            }
          }
        });
        setExpiringUfoIds(newExpiringUfoIds);
        
        return cleanedEmojis;
      });

      // Check if we need to spawn more emojis (periodic check)
      if (isAutoMode && gameConfig?.totalEmojisForLevel) {
        const currentEmojisCount = emojisRef.current.length;
        const { totalEmojisForLevel, maxConcurrentEmojis } = gameConfig;
        
        if (currentLevelRescuedCount < totalEmojisForLevel && currentEmojisCount < maxConcurrentEmojis) {
          console.log(`🔄 Periodic spawn check triggered: rescued=${currentLevelRescuedCount}/${totalEmojisForLevel}, onScreen=${currentEmojisCount}/${maxConcurrentEmojis}`);
          setTimeout(() => {
            spawnNewEmojiIfNeeded();
          }, 0);
        }
      }

      // Debug: Log emoji statuses to see what's happening
      if (emojisRef.current.length > 0) {
        console.log(`🔍 Current emojis:`, emojisRef.current.map(emoji => ({
          id: emoji.id,
          status: emoji.status,
          isActive: emoji.isActive,
          timeElapsed: currentTime - emoji.startTime,
          pathDuration: emoji.path[emoji.path.length - 1].time
        })));
      }

      // Check game status (with continuous spawning support)
      console.log(`🎯 About to check game status with params:`, {
        emojisCount: emojisRef.current.length,
        totalEmojisForLevel: gameConfig?.totalEmojisForLevel,
        currentLevelRescuedCount,
        currentLevel
      });
      
      const newGameStatus = emojiRescueService.getGameStatus(
        emojisRef.current, 
        0, // No tap limit - always pass 0
        gameConfig?.totalEmojisForLevel, 
        currentLevelRescuedCount
      );
      
      // Debug logging for continuous spawning
      if (gameConfig?.totalEmojisForLevel) {
        console.log(`📊 Level Progress: ${currentLevelRescuedCount}/${gameConfig.totalEmojisForLevel} rescued, ${emojisRef.current.length} on screen, Status: ${newGameStatus}`);
      }
      
      if (newGameStatus !== 'playing') {
        setGameStatus(newGameStatus);
        // Important: Stop the animation loop immediately if game is no longer playing
        if (animationRef.current) {
          cancelAnimationFrame(animationRef.current);
        }
        setIsPlaying(false); // Explicitly stop playing here
        return; // Stop further processing in this frame
      }

      animationRef.current = requestAnimationFrame(animate);
    };

    animationRef.current = requestAnimationFrame(animate);
    return () => {
      if (animationRef.current) cancelAnimationFrame(animationRef.current);
    };
  }, [isPlaying, gameConfig, gameStartTime, isAutoMode, spawnNewEmojiIfNeeded, currentLevelRescuedCount]);

  // UPDATED: Memoized components for emojis only (filter out vanished UFOs)
  const gameComponents = useMemo(() => {
    const currentTime = performance.now();
    const UFO_VANISH_DELAY = 500; // milliseconds
    
    // Filter out rescued emojis (fallen UFOs) that should have vanished
    const visibleEmojis = emojis.filter(item => {
      if (item.status === 'rescued' && item.rescuedTime) {
        const timeSinceRescue = currentTime - item.rescuedTime;
        return timeSinceRescue < UFO_VANISH_DELAY;
      }
      return true; // Show all non-rescued emojis
    });
    
    return visibleEmojis.map(item => {
      // Check if this UFO is expiring soon using the state from animation loop
      const isExpiringSoon = expiringUfoIds.has(item.id);
      
      return (
        <MemoizedEmoji
          key={item.id}
          id={item.id}
          position={item.position}
          status={item.status as any} // Type assertion for compatibility
          emoji={item.emoji}
          ufoType={item.ufoType}
          onTap={handleTap}
          isExpiringSoon={isExpiringSoon}
        />
      );
    });
  }, [emojis, handleTap, expiringUfoIds]);

  useEffect(() => {
    if (gameStatus !== 'playing') {
      gameAudio.stopBackgroundMusic();
    }
  }, [gameStatus]);

  useEffect(() => {
    const playGameStatusSound = async () => {
      try {
        if (gameStatus === 'lost') {
          await gameAudio.playGameOverSound();
        } else if (gameStatus === 'won') {
          await gameAudio.playReadyLevelSound();
        }
      } catch (error) {
        console.error('Error playing game status sound:', error);
      }
    };

    playGameStatusSound();
  }, [gameStatus]);

  useEffect(() => {
    // Initialize audio system
    const initializeAudio = async () => {
      try {
        await gameAudio.initialize();
        console.log('Audio initialized in PlayGame');
      } catch (error) {
        console.error('Error initializing audio in PlayGame:', error);
      }
    };

    initializeAudio();

    // Cleanup on unmount
    return () => {
      gameAudio.cleanup();
    };
  }, []); // Empty dependency array - only run on mount

  const handleMiss = useCallback((e: React.TouchEvent | React.MouseEvent) => {
    // e.preventDefault(); // Removed for desktop click debugging

    // If the event target is a game object component or its child, do not register as a miss
    const target = e.target as HTMLElement;
    const isGameObjectTap = target.closest('[data-bee-id]') || target.closest('[data-emoji-id]');
    if (isGameObjectTap) {
      return;
    }
    // console.log('[PlayGame] handleMiss - Processing as a miss.');

    if ((e.target as HTMLElement).tagName === 'BUTTON') return;
    // console.log('[PlayGame] handleMiss - After button check.');
    if (!isPlaying || gameStatus !== 'playing') {
      // console.log('[PlayGame] handleMiss - Not playing or game status is not playing. isPlaying:', isPlaying, ' gameStatus:', gameStatus);
      return;
    }
    // console.log('[PlayGame] handleMiss - Game is playing.');

    const now = Date.now();
    // console.log('[PlayGame] handleMiss - now:', now, ' lastTapTime.current:', lastTapTime.current, ' tapCooldown:', tapCooldown);
    if (now - lastTapTime.current < tapCooldown) {
      // console.log('[PlayGame] handleMiss - Tap cooldown active, returning.');
      return;
    }
    lastTapTime.current = now;
    // console.log('[PlayGame] handleMiss - Tap cooldown passed. New lastTapTime:', lastTapTime.current);

    gameAudio.playMissedTapSound();
    // console.log('[PlayGame] handleMiss - Play miss sound.');

    // Get tap coordinates safely
    let clientX: number, clientY: number;
    if ('touches' in e) {
      clientX = e.touches[0].clientX;
      clientY = e.touches[0].clientY;
    } else {
      clientX = e.clientX;
      clientY = e.clientY;
    }

    // Get the parent div safely
    const parentDiv = (e.currentTarget as HTMLElement)?.getBoundingClientRect();
    if (!parentDiv) {
      console.warn('Could not get parent div bounds');
      return;
    }

    const tapX = clientX - parentDiv.left;
    const tapY = clientY - parentDiv.top;

    // Removed tap limit - no need to decrement remainingTaps

    // Clear any existing timeout
    if (tapEffectTimeoutRef.current) {
      clearTimeout(tapEffectTimeoutRef.current);
    }

    setTapEffect({
      x: tapX,
      y: tapY,
      visible: true,
      tapSign: '☠️'
    });

    // Set new timeout to hide the effect
    tapEffectTimeoutRef.current = setTimeout(() => {
      setTapEffect(prev => ({ ...prev, visible: false }));
    }, 500);
  }, [gameStatus, isPlaying]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (tapEffectTimeoutRef.current) {
        clearTimeout(tapEffectTimeoutRef.current);
      }
    };
  }, []);

  // Add window resize handler
  useEffect(() => {
    const handleResize = () => {
      const newSize = {
        width: window.innerWidth,
        height: window.innerHeight
      };
      setWindowSize(newSize);
      // Force re-render of bees when window size changes
      setEmojis(prevBees => [...prevBees]);
    };

    window.addEventListener('resize', handleResize);
    // Initial size calculation
    handleResize();
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const loadPathData = async () => {
      // Only load path data for user-created games (non-auto mode)
      if (actualGameId && !isAutoMode) {
        const gameData = await gameService.loadGameData(actualGameId, windowSize);
        if (!gameData) return;

        if (gameService.isGameExpired(gameData)) {
          setGameStatus('expired');
          return;
        }

        console.log("single time", gameData.isSinglePlay);

        setTotalAttempts(gameData.totalAttempts);
        setSuccessfulAttempts(gameData.successfulAttempts);
        setPlayerName(gameData.playerName);
        setIsSinglePlay(gameData.isSinglePlay);
        setWinMessage(gameData.winMessage);
        setLoseMessage(gameData.loseMessage);
        
        // Play ready level sound for user-created games
        try {
          await gameAudio.initialize();
          await gameAudio.playReadyLevelSound();
          console.log('Ready level audio started for user-created game');
        } catch (error) {
          console.error('Error playing ready level sound for user-created game:', error);
        }
      }
    };

    loadPathData();
  }, [actualGameId, navigate, windowSize.width, windowSize.height, isAutoMode]);

  // Update winning handler
  useEffect(() => {
    const updateSuccessCount = async () => {
      if (gameStatus === 'won' && actualGameId && !isAutoMode) {
        try {
          await gameService.incrementSuccess(actualGameId);
          setSuccessfulAttempts(prev => prev + 1);
        } catch (error) {
          console.error('Error updating success count:', error);
        }
      }
    };

    updateSuccessCount();
  }, [gameStatus, actualGameId, isAutoMode]);

  // Add this after the useEffect that sets banners/confetti for gameStatus
  useEffect(() => {
    // Only for auto mode with a session
    if (!isAutoMode || !sessionId) return;
    if (gameStatus !== 'won' && gameStatus !== 'lost') return;

    const updateSession = async () => {
      const updateData = {
        status: gameStatus,
        currentLevel,
        highestLevel: Math.max(currentLevel, autoGameState.highestLevel),
      };
      try {
        await levelDbService.updateLevelSession(sessionId, updateData);
        
        // Save emoji collection when level is completed (won or lost)
        if (Object.keys(rescuedEmojis).length > 0) {
          await levelDbService.updateEmojiCollection(sessionId, rescuedEmojis);
        }
        
        // Save super emoji collection when level is completed (won or lost)
        if (Object.keys(superEmojiCollection).length > 0) {
          await levelDbService.updateSuperEmojiCollection(sessionId, superEmojiCollection);
        }
      } catch (e) {
        console.error('Failed to update level session on game over:', e);
      }
    };
    updateSession();
    // Only run when gameStatus, currentLevel, etc. change
  }, [gameStatus, isAutoMode, sessionId, currentLevel, autoGameState.highestLevel, rescuedEmojis, superEmojiCollection]);

  // Stop ready level sound when game actually starts playing
  useEffect(() => {
    if (isPlaying && gameStatus === 'playing') {
      // Stop the ready level sound when the game transitions to playing state
      gameAudio.stopGameStatusSounds();
      console.log('Ready level sound stopped - game is now playing');
    }
  }, [isPlaying, gameStatus]);

  // Initialize game
  const initializeGame = useCallback(async () => {
    const startTime = performance.now();
    setGameStartTime(startTime);

    // Reset all states
    setIsTransitioning(false);
    setShowConfetti(false);
    setLevelCompletionMessage('');
    setShowingLevelCompletion(false);
    // Reset isFirstLevel when starting a new game
    setIsFirstLevel(true);

    // Stop any previously playing game status sounds
    gameAudio.stopGameStatusSounds();

    // Clear any lingering tap effects, rescue effects, celebrating emojis
    setTapEffect({ x: 0, y: 0, visible: false, tapSign: '☠️' });
    setRescueEffects([]);
    setCelebratingEmojis([]);
    celebratedEmojiIdsRef.current.clear(); // Reset celebrated emoji tracking for new level
    // Reset spawning counters for new level
    setSpawnedEmojiCount(0);
    setNextEmojiIndex(0);
    setCurrentLevelRescuedCount(0);
    celebratingIdCounter.current = 0;
    // Reset emoji collection only when starting a completely new game session
    // For auto mode, collection is loaded from session data in useEffect above
    if (!isAutoMode) {
      setRescuedEmojis({});
    }
    if (tapEffectTimeoutRef.current) {
      clearTimeout(tapEffectTimeoutRef.current);
    }

    try {
      if (isAutoMode) {
        const levelToUse = currentLevel;
        console.log(`[PlayGame] initializeGame: Auto mode, initializing level: ${levelToUse}`);
        console.log(`[PlayGame] Current states - currentLevel: ${currentLevel}, autoGameState.currentLevel: ${autoGameState.currentLevel}`);
        const levelConfig = LevelService.getLevelConfig(levelToUse);
        console.log(`[PlayGame] Generated level config:`, levelConfig);
        const { paths, config } = await LevelService.initializeLevelWithEmojis(
          levelToUse,
          windowSize
        );
        setGameConfig(config);
        const initialBees = emojiRescueService.initializeCapturedEmojisWithIntervals(
          paths,
          startTime,
          LevelService.getEmojiLevelConfig(levelConfig.level)
        );
        setEmojis(initialBees);
        // Initialize spawning counter with the number of initial emojis
        setSpawnedEmojiCount(initialBees.length);
        setNextEmojiIndex(initialBees.length);
        // Removed remainingTaps - no tap limit
      } else {
        // For user-generated games, increment attempts when starting
        if (actualGameId) {
          await gameService.incrementAttempts(actualGameId);
          setTotalAttempts(prev => prev + 1);
        }

        // Load game data for user mode
        const gameData = await gameService.loadGameData(actualGameId, windowSize);
        if (!gameData) return;
        
        // Ensure path points have rotation
        const pathWithRotation = gameData.points.map((point: Point, index: number, arr: Point[]) => {
          if (index === arr.length - 1) return point;
          const nextPoint = arr[index + 1];
          const rotation = Math.atan2(nextPoint.y - point.y, nextPoint.x - point.x) * (180 / Math.PI);
          return { ...point, rotation };
        });
        const paths = [pathWithRotation];
        const config = emojiRescueService.calculateGameConfig(1, paths[0][paths[0].length - 1].time, LevelService.getEmojiLevelConfig(1).theme);
        setGameConfig(config);
        // Initialize single emoji for user path
        const initialBees = emojiRescueService.initializeCapturedEmojis(
          paths,
          startTime,
          false,
          LevelService.getEmojiLevelConfig(1).theme
        );
        setEmojis(initialBees);
        // Removed remainingTaps - no tap limit for user paths
      }

      setIsPlaying(true);
      setGameStatus('playing');
      await gameAudio.playBackgroundMusic();
    } catch (error) {
      console.error('Error initializing game:', error);
      setIsTransitioning(false);
    }
  }, [currentLevel, windowSize, isAutoMode, actualGameId]);

  return (
    <GameContainer onMiss={handleMiss}>
      <GameStyles />
      <GameBackground isPlaying={isPlaying} gameStatus={gameStatus} currentTheme={currentTheme} />
      
      {showConfetti && (
        <GameConfetti width={windowSize.width} height={windowSize.height} />
      )}
      
      <GameCanvas canvasRef={canvasRef} />
      
      <div style={{ zIndex: 5 }}>
        {gameStatus === 'expired' ? (
          <GameExpiredScreen onCreateNew={() => navigate('/')} />
        ) : (
          <>
            {isPlaying && !showingLevelCompletion && (
              <GameHeader 
                progress={progress}
                superEmojiCollection={superEmojiCollection}
                onSuperEmojiClick={handleSuperEmojiActivation}
                level={currentLevel}
                isAutoMode={isAutoMode}
                emojiMode={true}
              />
            )}

            {/* Show start screen for first level in auto/session mode, or for user-created games */}
            {!isPlaying && !isTransitioning && !showingLevelCompletion && (
              (isAutoMode && isFirstLevel) || (!isAutoMode && actualGameId) ? (
                <GameStartScreen 
                  playerName={playerName}
                  isSinglePlay={isSinglePlay}
                  totalAttempts={totalAttempts}
                  successfulAttempts={successfulAttempts}
                  onStart={initializeGame}
                  isAutoMode={isAutoMode}
                  level={isAutoMode ? currentLevel : undefined}
                  themeName={currentTheme?.name}
                  themeDescription={currentTheme?.description}
                />
              ) : null
            )}

            {isPlaying && !showingLevelCompletion && gameComponents}

            <TapEffect 
              id="tap-effect"
              x={tapEffect.x} 
              y={tapEffect.y} 
              visible={tapEffect.visible} 
              type={tapEffect.tapSign === '💥' ? 'success' : 'fail'} 
            />

            {/* NEW: Rescue Effects for emoji mode */}
            {rescueEffects.map((effect) => (
              <RescueEffect 
                key={effect.id || effect.timestamp}
                effect={effect}
                onEffectComplete={() => {
                  console.log('Rescue effect completed:', effect.id || effect.timestamp);
                  setRescueEffects(prev => prev.filter(e => 
                    effect.id ? e.id !== effect.id : e.timestamp !== effect.timestamp
                  ));
                }}
              />
            ))}

            {/* NEW: Celebrating Emojis at bottom */}
            {celebratingEmojis.map((celebratingEmoji) => (
              <CelebratingEmoji
                key={celebratingEmoji.id}
                celebratingEmoji={celebratingEmoji}
                onRemove={removeCelebratingEmoji}
                windowHeight={windowSize.height}
                windowWidth={windowSize.width}
              />
            ))}

            {/* Show Game Over screen (win/loss) */}
            {showingLevelCompletion && (
              <GameOverScreen
                type={gameStatus === 'won' ? 'won' : 'lost'}
                message={levelCompletionMessage}
                onPlayAgain={() => {
                  setTapEffect({ x: 0, y: 0, visible: false, tapSign: '☠️' });
                  setShowConfetti(false);
                  setShowingLevelCompletion(false);
                  window.location.reload();
                }}
                onCreateNew={() => {
                  setTapEffect({ x: 0, y: 0, visible: false, tapSign: '☠️' });
                  setShowConfetti(false);
                  setShowingLevelCompletion(false);
                  navigate('/');
                }}
                customButton={gameStatus === 'won' && isAutoMode && LevelService.hasMoreLevels(currentLevel) ? {
                  text: `Start Level ${currentLevel + 1}`,
                  onClick: handleStartNextLevel
                } : undefined}
                currentLevel={isAutoMode ? currentLevel : undefined}
                highestLevel={isAutoMode ? (gameStatus === 'lost' ? currentLevel - 1 : currentLevel) : undefined}
                rescuedEmojis={rescuedEmojis}
                themeName={currentTheme?.name}
                themeDescription={currentTheme?.description}
              />
            )}

            {/* Game Footer with Super Emojis - Outside Game Container */}
            {isPlaying && !showingLevelCompletion && (
              <GameFooter 
                superEmojiCollection={superEmojiCollection} 
                onSuperEmojiClick={handleSuperEmojiActivation} 
              />
            )}
          </>
        )}

        {/* Super Emoji Effects Layer */}
        <SuperEmojiEffects 
          effects={superEmojiEffects}
          windowSize={windowSize}
        />
      </div>

      <style>
        {`
          @keyframes fadeIn {
            from { opacity: 0; }
            to { opacity: 1; }
          }
          @keyframes slideUp {
            from { 
              opacity: 0;
              transform: translateY(20px);
            }
            to { 
              opacity: 1;
              transform: translateY(0);
            }
          }
        `}
      </style>
    </GameContainer>
  );
}
